export type RewardsMasterMetric = 'links' | 'volume' | 'commission' | 'discount'
export type RewardsMasterPeriod = 'day' | 'week' | 'month' | 'quarter' | 'semester' | 'year'

export type ChartPoint = { time: number; value: number }

export type PeriodBucket = {
  startMs: number
  endMs: number
  timeSec: number
  label: string
}

const PERIOD_CONFIG: Record<RewardsMasterPeriod, { label: string; buckets: number }> = {
  day: { label: 'Dia', buckets: 30 },
  week: { label: 'Semana', buckets: 12 },
  month: { label: 'Mes', buckets: 12 },
  quarter: { label: 'Trimestre', buckets: 8 },
  semester: { label: 'Semestre', buckets: 6 },
  year: { label: 'Anual', buckets: 5 },
}

export const REWARDS_MASTER_PERIODS: Array<{ value: RewardsMasterPeriod; label: string }> = (
  Object.entries(PERIOD_CONFIG) as Array<[RewardsMasterPeriod, { label: string }]>
).map(([value, cfg]) => ({ value, label: cfg.label }))

export function getPeriodBuckets(period: RewardsMasterPeriod, nowMs: number = Date.now()): PeriodBucket[] {
  const cfg = PERIOD_CONFIG[period]
  const base = startOfPeriod(period, new Date(nowMs))
  const out: PeriodBucket[] = []
  for (let i = cfg.buckets - 1; i >= 0; i -= 1) {
    const start = addPeriod(period, base, -i)
    const end = addPeriod(period, start, 1)
    out.push({
      startMs: start.getTime(),
      endMs: end.getTime(),
      timeSec: Math.floor(start.getTime() / 1000),
      label: formatBucketLabel(period, start),
    })
  }
  return out
}

export function bucketEvents(events: Array<{ timeMs: number; value: number }>, buckets: PeriodBucket[]): ChartPoint[] {
  if (!events.length || !buckets.length) return []
  const totals = new Array(buckets.length).fill(0)
  for (const ev of events) {
    const time = Number(ev.timeMs)
    if (!Number.isFinite(time)) continue
    for (let i = 0; i < buckets.length; i += 1) {
      const b = buckets[i]
      if (time >= b.startMs && time < b.endMs) {
        totals[i] += Number(ev.value || 0)
        break
      }
    }
  }
  return buckets.map((b, idx) => ({ time: b.timeSec, value: totals[idx] }))
}

export function sumEventsInRange(events: Array<{ timeMs: number; value: number }>, startMs: number, endMs: number): number {
  let total = 0
  for (const ev of events) {
    const time = Number(ev.timeMs)
    if (!Number.isFinite(time)) continue
    if (time >= startMs && time < endMs) total += Number(ev.value || 0)
  }
  return total
}

export function getPeriodRange(buckets: PeriodBucket[]): { startMs: number; endMs: number } | null {
  if (!buckets.length) return null
  return { startMs: buckets[0].startMs, endMs: buckets[buckets.length - 1].endMs }
}

export function getPeriodLabel(period: RewardsMasterPeriod): string {
  return PERIOD_CONFIG[period]?.label || period
}

function startOfPeriod(period: RewardsMasterPeriod, date: Date): Date {
  switch (period) {
    case 'day':
      return startOfDay(date)
    case 'week':
      return startOfWeek(date)
    case 'month':
      return startOfMonth(date)
    case 'quarter':
      return startOfQuarter(date)
    case 'semester':
      return startOfSemester(date)
    case 'year':
      return startOfYear(date)
    default:
      return startOfDay(date)
  }
}

function addPeriod(period: RewardsMasterPeriod, base: Date, amount: number): Date {
  switch (period) {
    case 'day':
      return addDays(base, amount)
    case 'week':
      return addDays(base, amount * 7)
    case 'month':
      return addMonths(base, amount)
    case 'quarter':
      return addMonths(base, amount * 3)
    case 'semester':
      return addMonths(base, amount * 6)
    case 'year':
      return addMonths(base, amount * 12)
    default:
      return addDays(base, amount)
  }
}

function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function startOfWeek(date: Date): Date {
  const d = startOfDay(date)
  const day = d.getDay()
  const diff = (day + 6) % 7
  d.setDate(d.getDate() - diff)
  return d
}

function startOfMonth(date: Date): Date {
  const d = startOfDay(date)
  d.setDate(1)
  return d
}

function startOfQuarter(date: Date): Date {
  const d = startOfMonth(date)
  const month = d.getMonth()
  const quarterStart = month - (month % 3)
  d.setMonth(quarterStart, 1)
  return d
}

function startOfSemester(date: Date): Date {
  const d = startOfMonth(date)
  const month = d.getMonth()
  const semesterStart = month - (month % 6)
  d.setMonth(semesterStart, 1)
  return d
}

function startOfYear(date: Date): Date {
  const d = startOfDay(date)
  d.setMonth(0, 1)
  return d
}

function addDays(date: Date, amount: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + amount)
  return d
}

function addMonths(date: Date, amount: number): Date {
  const d = new Date(date)
  const day = d.getDate()
  d.setDate(1)
  d.setMonth(d.getMonth() + amount)
  const maxDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  d.setDate(Math.min(day, maxDay))
  return d
}

function formatBucketLabel(period: RewardsMasterPeriod, date: Date): string {
  if (period === 'day') {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }
  if (period === 'week') {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }
  if (period === 'month') {
    const label = date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
    return label.replace('.', '')
  }
  if (period === 'quarter') {
    const q = Math.floor(date.getMonth() / 3) + 1
    return `T${q}/${date.getFullYear()}`
  }
  if (period === 'semester') {
    const s = Math.floor(date.getMonth() / 6) + 1
    return `S${s}/${date.getFullYear()}`
  }
  return String(date.getFullYear())
}
